import { z } from 'zod';
import { E164_REGEX } from '../lib/phone';
import {
  normalizeStoreContactPhone,
  STORE_CONTACT_INTERNATIONAL_INPUT_MAX_LENGTH,
  STORE_CONTACT_LOCAL_INPUT_MAX_LENGTH,
  type StoreContactPhoneFieldInput,
} from '../lib/store-contact-phone';

/**
 * F-033 — Zod schema for the admin store-settings contact-methods form.
 *
 * RHF stores country-first field objects. This schema validates each
 * local number against its selected country and transforms the form
 * shape back to the normalized F-007 service boundary.
 *
 * The `atLeastOneRequired` refinement is an INTENTIONAL UX constraint at
 * the form layer (design §3 W3 + §12 W3): a merchant who clears their
 * only contact method is almost certainly confused, and the form should
 * push back before they submit. The DB accepts both-null because an
 * in-progress save may legitimately have one field cleared and the
 * other not yet typed — the publish gate (§2.4, SQLSTATE P0003) is the
 * authoritative enforcement point.
 *
 * The refined error is surfaced on the `whatsapp.localNumber` path for
 * keyboard-focus reachability (a11y). Test-engineer: this is the
 * load-bearing landmine for §12.12 (wa.me / tel: `+` handling) on the
 * Zod side — see `phone.test.ts` for the Arabic-Indic digit rejection.
 */
const storeContactCountrySchema = z.enum([
  'IL',
  'PS',
  'JO',
  'SY',
  'LB',
  'EG',
  'TR',
  'US',
  'INTERNATIONAL',
]);

export const storeContactPhoneFieldSchema = z.object({
  country: storeContactCountrySchema,
  localNumber: z.string(),
}) satisfies z.ZodType<StoreContactPhoneFieldInput>;

export interface ContactMethodsFormInput {
  readonly whatsapp: StoreContactPhoneFieldInput;
  readonly phone: StoreContactPhoneFieldInput;
}

export interface NormalizedContactMethods {
  readonly whatsappNumber: string | null;
  readonly phoneNumber: string | null;
}

const fieldErrorKey = (
  field: 'whatsapp' | 'phone',
  reason: 'invalidForCountry' | 'invalidInternational' | 'localTooLong' | 'internationalTooLong',
): string => {
  if (reason === 'invalidInternational') {
    return `admin.storeSettings.errors.${field}InternationalInvalid`;
  }
  if (reason === 'internationalTooLong') {
    return `admin.storeSettings.errors.${field}InternationalTooLong`;
  }
  if (reason === 'localTooLong') {
    return `admin.storeSettings.errors.${field}LocalTooLong`;
  }
  return `admin.storeSettings.errors.${field}InvalidForCountry`;
};

export const contactMethodsSchema = z
  .object({
    whatsapp: storeContactPhoneFieldSchema,
    phone: storeContactPhoneFieldSchema,
  })
  .superRefine((input, ctx) => {
    (['whatsapp', 'phone'] as const).forEach((field) => {
      const value = input[field];
      const rawLength = value.localNumber.trim().length;
      if (value.country === 'INTERNATIONAL') {
        if (rawLength > STORE_CONTACT_INTERNATIONAL_INPUT_MAX_LENGTH) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: fieldErrorKey(field, 'internationalTooLong'),
            path: [field, 'localNumber'],
          });
        }
        return;
      }
      if (rawLength > STORE_CONTACT_LOCAL_INPUT_MAX_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: fieldErrorKey(field, 'localTooLong'),
          path: [field, 'localNumber'],
        });
      }
    });
  })
  .transform((input, ctx): NormalizedContactMethods => {
    const whatsapp = normalizeStoreContactPhone(input.whatsapp);
    const phone = normalizeStoreContactPhone(input.phone);

    if (!whatsapp.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: fieldErrorKey(
          'whatsapp',
          whatsapp.reason === 'invalidInternational' ? 'invalidInternational' : 'invalidForCountry',
        ),
        path: ['whatsapp', 'localNumber'],
      });
    }

    if (!phone.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: fieldErrorKey(
          'phone',
          phone.reason === 'invalidInternational' ? 'invalidInternational' : 'invalidForCountry',
        ),
        path: ['phone', 'localNumber'],
      });
    }

    const normalized = {
      whatsappNumber: whatsapp.ok ? whatsapp.e164 : null,
      phoneNumber: phone.ok ? phone.e164 : null,
    };

    if (
      whatsapp.ok &&
      phone.ok &&
      normalized.whatsappNumber === null &&
      normalized.phoneNumber === null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'admin.storeSettings.errors.atLeastOneRequired',
        path: ['whatsapp', 'localNumber'],
      });
    }

    return normalized;
  });

export const normalizedContactMethodsSchema = z
  .object({
    whatsappNumber: z
      .string()
      .trim()
      .nullable()
      .refine((v) => v === null || E164_REGEX.test(v), {
        message: 'admin.storeSettings.errors.whatsappInvalid',
      }),
    phoneNumber: z
      .string()
      .trim()
      .nullable()
      .refine((v) => v === null || E164_REGEX.test(v), {
        message: 'admin.storeSettings.errors.phoneInvalid',
      }),
  })
  .refine((v) => v.whatsappNumber !== null || v.phoneNumber !== null, {
    message: 'admin.storeSettings.errors.atLeastOneRequired',
    path: ['whatsappNumber'],
  }) satisfies z.ZodType<NormalizedContactMethods>;

export type ContactMethodsFormValues = NormalizedContactMethods;
